import React from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingIndicator } from 'atoms';

import { IOverviewWidgetState } from './types';
import {
  WidgetCard,
  WidgetLoader,
  WidgetPlaceholder,
  WidgetTitle,
} from './overview.styled';

interface IOverviewWidgetShellProps<T> {
  /** Localized widget heading. */
  title: string;
  /** Localized accessible label for the clickable shell (announces the action). */
  ariaLabel: string;
  /** The widget's three-state model (loading / empty / populated). */
  state: IOverviewWidgetState<T>;
  /** Invoked on click and on Enter/Space (native button) — navigates to a tab. */
  onActivate: () => void;
  /** Renders the populated body once `state.data` is present and non-empty. */
  children: (data: T) => React.ReactNode;
}

/**
 * Shared shell for the three Overview summary widgets (attendance, performance,
 * money). It owns the three-state rendering so each widget only describes its
 * populated body:
 *
 *   1. `loading` — the initial load or a background refresh. The loader is shown
 *      and any previously cached body is hidden, so a refreshing widget never
 *      flashes stale figures (Requirement 1.3, 1.4).
 *   2. `isEmpty` — the summary query resolved with no usable data: a localized
 *      empty message is shown (Requirement 1.5).
 *   3. otherwise the widget is populated from `state.data`.
 *
 * The shell is a native `<button>` (see `WidgetCard`) so it is focusable,
 * keyboard-activatable (Enter/Space), and navigates to its dedicated tab on
 * activation (Requirement 2.4). Loading is checked before data so the refresh
 * case wins even while cached `data` is still in the Apollo cache.
 */
export const OverviewWidgetShell = <T,>({
  title,
  ariaLabel,
  state,
  onActivate,
  children,
}: IOverviewWidgetShellProps<T>): React.ReactElement => {
  const { t } = useTranslation();

  const renderBody = (): React.ReactNode => {
    if (state.loading) {
      return (
        <WidgetLoader>
          <LoadingIndicator />
        </WidgetLoader>
      );
    }
    if (state.isEmpty || !state.data) {
      return (
        <WidgetPlaceholder>
          {t('pages.groupDetail.overview.widgetEmpty')}
        </WidgetPlaceholder>
      );
    }
    return children(state.data);
  };

  return (
    <WidgetCard type="button" onClick={onActivate} aria-label={ariaLabel}>
      <WidgetTitle>{title}</WidgetTitle>
      {renderBody()}
    </WidgetCard>
  );
};
